import React, { useState, useEffect } from 'react';
import {
    Box, Card, Typography, Divider, FormControlLabel, Switch,
    ToggleButton, ToggleButtonGroup, FormHelperText, Button,
    TextField,
    InputAdornment
} from '@mui/material';
import { openSnackbar } from '../../redux/slices/common';
import { useDispatch } from 'react-redux';
import { updateOrderMinimumSettings, fetchOrderMinimumSettings } from '../../redux/slices/CreditCardFee';

const ORDER_TYPE_CONFIG = {
    asap: { label: 'ASAP Minimum Order Value', key: 'asap' },
    scheduled: { label: 'Scheduled Minimum Order Value', key: 'schedule' },
    pickup: { label: 'Pickup Minimum Order Value', key: 'pickup' },
};

const OrderSetting = () => {
    const dispatch = useDispatch();
    const [orderTypeError, setOrderTypeError] = useState('');
    const [minValueError, setMinValueError] = useState('');
    const [isDirty, setIsDirty] = useState(false);
    const [settings, setSettings] = useState({
        enabled: true,
        orderType: 'asap',
        asap: '',
        schedule: '',
        pickup: '',
    });
    console.log("settings", settings);

    const handleToggleChange = (event) => {
        setSettings(prev => ({
            ...prev,
            enabled: event.target.checked,
        }));
        setIsDirty(true);
    };

    const handleOrderTypeChange = (event) => {
        setSettings(prev => ({
            ...prev,
            orderType: event.target.value,
        }));
        setOrderTypeError('');
        setMinValueError('');
        setIsDirty(true);
    };

    const handleMinValueChange = (event) => {
        const { value } = event.target;
        const apiKey = ORDER_TYPE_CONFIG[settings.orderType]?.key;
        if (apiKey) {
            setSettings(prev => ({
                ...prev,
                [apiKey]: value,
            }));
        }
        setMinValueError('');
        setIsDirty(true);
    };

    const handleSave = () => {
        const apiKey = ORDER_TYPE_CONFIG[settings.orderType]?.key;
        if (!apiKey || settings[apiKey] === '' || settings[apiKey] === null) {
            setMinValueError('Minimum order value is required');
            return;
        }
        if (Number(settings[apiKey]) < 0) {
            setMinValueError('Minimum order value must be 0 or greater');
            return;
        }

        const payload = {
            enabled: settings.enabled,
            asap: Number(settings.asap) || 0,
            schedule: Number(settings.schedule) || 0,
            pickup: Number(settings.pickup) || 0,
        };

        console.log('Save payload:', payload);
        // TODO: dispatch(updateOrderMinimumSettings(payload))
        dispatch(updateOrderMinimumSettings(payload)).unwrap().then((res) => {
            showError(res?.message, 'success')
            dispatch(fetchOrderMinimumSettings())
        }).catch((err) => {
            showError(err?.message, 'error')

        })
        setIsDirty(false);
    };

    const showError = (message, errortype) => {
        if (errortype === 'success') {
            dispatch(openSnackbar({ content: message, color: 'success', icon: 'check' }))
        }
        if (errortype === 'error') {
            dispatch(openSnackbar({ content: message, color: 'error', icon: 'warning' }))
        }
    }

    useEffect(() => {
        dispatch(fetchOrderMinimumSettings()).unwrap().then((res) => {
            console.log("res", res);

            setSettings({
                enabled: res?.enabled,
                orderType: res?.orderType || "asap",
                asap: res?.asap,
                schedule: res?.schedule,
                pickup: res?.pickup,
            })
        }).catch(() => { })

    }, [])
    console.log("setting", settings);


    const currentApiKey = ORDER_TYPE_CONFIG[settings.orderType]?.key;
    const currentMinValue = currentApiKey ? settings[currentApiKey] : '';
    const currentLabel = ORDER_TYPE_CONFIG[settings.orderType]?.label;

    return (
        <Box>
            <Card sx={{ p: 3, borderRadius: "0.75rem !important", boxShadow: "none" }}>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Typography variant="h6" fontWeight={600} color="text.primary">
                        Order Minimum value
                    </Typography>

                    <FormControlLabel
                        control={<Switch checked={settings.enabled} onChange={handleToggleChange} />}
                        label=""
                        sx={{ m: 0 }}
                    />
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Box>
                    {/* Order Type Toggle */}
                    <Box sx={{ mb: 3 }}>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                        >
                            Order Type
                        </Typography>
                        <ToggleButtonGroup
                            value={settings.orderType}
                            exclusive
                            onChange={(e) => handleOrderTypeChange(e)}
                            aria-label="order type"
                            fullWidth
                            sx={{
                                '& .MuiToggleButtonGroup-grouped': {
                                    '&.Mui-selected': {
                                        backgroundColor: 'primary.main',
                                        color: 'white.main',
                                        '&:hover': {
                                            backgroundColor: 'primary.main',
                                        },
                                    },
                                    p: 1,
                                    fontWeight: 500,
                                },
                            }}
                        >
                            <ToggleButton value="asap" aria-label="asap">
                                ASAP
                            </ToggleButton>
                            <ToggleButton value="scheduled" aria-label="scheduled">
                                Scheduled
                            </ToggleButton>
                            <ToggleButton value="pickup" aria-label="pickup">
                                Pickup
                            </ToggleButton>
                        </ToggleButtonGroup>
                        {
                            orderTypeError && <FormHelperText error>{orderTypeError}</FormHelperText>
                        }
                    </Box>

                    {/* Minimum Value Input — changes based on selected order type */}
                    <Box sx={{ mb: 3 }}>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                        >
                            {currentLabel}
                        </Typography>
                        <TextField
                            fullWidth
                            type="number"
                            placeholder="Enter minimum order value"
                            value={currentMinValue}
                            onChange={handleMinValueChange}
                            // error={Boolean(minValueError)}
                            // helperText={minValueError}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">$</InputAdornment>
                                ),
                                inputProps: { min: 0 },
                            }}
                            size="small"
                        />
                    </Box>

                    {/* Save Changes Footer */}
                    <Box
                        component="footer"
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            mt: 2,
                        }}
                    >
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={!isDirty}
                            disableElevation
                            sx={{
                                opacity: isDirty ? 1 : 0.7,
                                transition: 'all 0.2s',
                                '&:not(:disabled):hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                                },
                            }}
                        >
                            Save Changes
                        </Button>
                    </Box>
                </Box>
            </Card>
        </Box>
    )
}

export default OrderSetting;